import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  HostBinding,
  inject,
  input,
  OnDestroy,
  output,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe, NgClass } from '@angular/common';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { SCHEDULER_CONFIG, SchedulerConfig } from './scheduler-config';
import { BeautySchedulerResourceTemplateDirective } from './beauty-scheduler-resource.directive';
import { BeautySchedulerEventTemplateDirective } from './beauty-scheduler-event.directive';
import { BeautySchedulerEventPopoverTemplateDirective } from './beauty-scheduler-event-popover.directive';

export type SchedulerViewMode = 'horizontal' | 'vertical';

export interface SchedulerResource {
  id: string;
  userId: string;
  title: string;
  color?: string;
  // Optional working hours per resource; if omitted, all slots are available
  workStart?: Date | string;
  workEnd?: Date | string;
  // Optional breaks per resource
  breaks?: {
    start: Date | string;
    end: Date | string;
    title?: string;
    color?: string;
  }[];
  extendedProps?: Record<string, any>;
}

export interface SchedulerEvent {
  id?: string;
  resourceId: string;
  groupId: string;
  start: Date | string;
  end: Date | string;
  title?: string;
  color?: string;
  oldStyle?: boolean;
  // Arbitrary external properties that consumers can attach to events
  extendedProps?: Record<string, any>;
}

@Component({
  selector: 'beautyapp-scheduler',
  standalone: true,
  imports: [CommonModule, NgClass, DatePipe, NzPopoverModule],
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulerComponent implements AfterViewInit, OnDestroy {
  // Custom projected templates from ng-content
  @ContentChild(BeautySchedulerResourceTemplateDirective)
  resourceTemplate?: BeautySchedulerResourceTemplateDirective;
  @ContentChild(BeautySchedulerEventTemplateDirective)
  eventTemplate?: BeautySchedulerEventTemplateDirective;
  @ContentChild(BeautySchedulerEventPopoverTemplateDirective)
  eventPopoverTemplate?: BeautySchedulerEventPopoverTemplateDirective;
  // Hovered resource index for highlighting row/column
  hoveredResourceIndex = signal<number | null>(null);
  // Hovered slot index for highlighting time row/column
  hoveredSlotIndex = signal<number | null>(null);

  onResourceHover(index: number | null) {
    this.hoveredResourceIndex.set(index);
  }

  onSlotHover(index: number | null) {
    this.hoveredSlotIndex.set(index);
  }

  // Inject global defaults if provided
  readonly _cfg = inject(SCHEDULER_CONFIG, { optional: true });

  // Apply CSS variables from config on host, if provided (merged: component config over injected)
  @HostBinding('style.--resource-min-width') get cssResourceMinWidth() {
    return this.mergedConfig().cssVars?.resourceMinWidth || null;
  }

  @HostBinding('style.--slot-height') get cssSlotHeight() {
    const explicit = this.mergedConfig().cssVars?.slotHeight;
    if (explicit) return explicit;
    return `${this.slotHeightPx()}px`;
  }

  @HostBinding('style.--row-height') get cssRowHeight() {
    return this.mergedConfig().cssVars?.rowHeight || null;
  }

  @HostBinding('style.--slot-width') get cssSlotWidth() {
    const explicit = this.mergedConfig().cssVars?.slotWidth;
    if (explicit) return explicit;
    return `${this.slotWidthPx()}px`;
  }

  @HostBinding('style.--event-gutter') get cssEventGutter() {
    return this.mergedConfig().cssVars?.eventGutter || null;
  }

  // Component-level config (overrides injected config)
  config = input<Partial<SchedulerConfig> | null>(null);
  readonly mergedConfig = computed<SchedulerConfig>(() => {
    const base = this._cfg ?? {};
    const local = this.config() ?? {};
    const cssVars = {
      ...(base.cssVars ?? {}),
      ...(local.cssVars ?? {}),
    } as SchedulerConfig['cssVars'];
    return {
      ...(base as SchedulerConfig),
      ...(local as SchedulerConfig),
      cssVars,
    };
  });

  // Signal-based inputs (Angular v17+)
  resources = input<SchedulerResource[]>([]);
  events = input<SchedulerEvent[]>([]);
  start = input.required<Date | string>();
  end = input.required<Date | string>();
  // Let explicit inputs override config if provided; otherwise, rely on mergedConfig
  slotMinutes = input<number | undefined>(undefined);
  viewMode = input<SchedulerViewMode | undefined>(undefined);
  // Text shown on non-working (off) slots; provided by the consumer (translated).
  offLabel = input<string>('');
  readonly effectiveSlotMinutes = computed(
    () => this.slotMinutes() ?? this.mergedConfig().slotMinutes ?? 30
  );
  readonly effectiveViewMode = computed<SchedulerViewMode>(
    () => this.viewMode() ?? this.mergedConfig().viewMode ?? 'horizontal'
  );
  readonly showNowLineEnabled = computed<boolean>(
    () => this.mergedConfig().showNowLine ?? true
  );

  // Pixel size of a single slot along the time axis. Mirrors the CSS var derivation
  // so events can be sized/positioned by their exact time rather than snapped to slots.
  readonly slotHeightPx = computed<number>(() => {
    const explicit = this.mergedConfig().cssVars?.slotHeight;
    const parsed = explicit ? parseFloat(explicit) : NaN;
    if (!isNaN(parsed)) return parsed;
    const m = this.effectiveSlotMinutes();
    if (m <= 20) return 40;
    if (m <= 30) return 52;
    if (m <= 60) return 100;
    return 48;
  });
  readonly slotWidthPx = computed<number>(() => {
    const explicit = this.mergedConfig().cssVars?.slotWidth;
    const parsed = explicit ? parseFloat(explicit) : NaN;
    if (!isNaN(parsed)) return parsed;
    const m = this.effectiveSlotMinutes();
    if (m <= 5) return 60;
    if (m <= 20) return 80;
    if (m <= 30) return 100;
    if (m <= 60) return 120;
    return 60;
  });
  // Pixels per minute along the active time axis (vertical in horizontal view, horizontal in vertical view).
  readonly pxPerMinute = computed<number>(() => {
    const slotPx =
      this.effectiveViewMode() === 'horizontal'
        ? this.slotHeightPx()
        : this.slotWidthPx();
    return slotPx / this.effectiveSlotMinutes();
  });

  // Outputs (signal-based outputs)
  eventClick = output<SchedulerEvent>();
  slotClick = output<{ resourceId: string; start: Date; end: Date }>();
  // Right-click on an empty slot; consumer opens a context menu of quick actions.
  slotContextMenu = output<{
    resourceId: string;
    start: Date;
    end: Date;
    available: boolean;
    originalEvent: MouseEvent;
  }>();
  // Drag&Drop outputs
  eventDrop = output<{
    event: SchedulerEvent;
    resourceId: string;
    start: Date;
    end: Date;
  }>();
  eventResize = output<{
    event: SchedulerEvent;
    resourceId: string;
    start: Date;
    end: Date;
  }>();
  eventDragStart = output<SchedulerEvent>();
  eventDragEnd = output<SchedulerEvent | null>();

  // Refs for horizontal scroll sync (signal-based view queries)
  headerScroller = viewChild<ElementRef<HTMLDivElement>>('headerScroller');
  bodyScroller = viewChild<ElementRef<HTMLDivElement>>('bodyScroller');
  grid = viewChild<ElementRef<HTMLDivElement>>('grid');
  schedulerBody = viewChild<ElementRef<HTMLDivElement>>('schedulerBody');

  // Pastel color palette used when event.color is not provided
  private readonly eventPalette = [
    '#e6f4ff',
    '#f9f0ff',
    '#fff7e6',
    '#f6ffed',
    '#e6fffb',
    '#fff1f0',
    '#f0f5ff',
    '#f0fff2',
    '#fffbe6',
    '#fef0f0',
  ];

  // Compute a stable color for the event to ensure different-looking events by default
  // Important: Do not let color change when the event is moved (time/resource updates).
  // Strategy: if ev.color is provided, use it. Otherwise, derive a stable color from a stable key:
  // - Prefer ev.id (stable across updates and expected in production)
  // - Fallback to ev.title if id is missing (best-effort stability)
  eventColor(ev: SchedulerEvent, res?: SchedulerResource): string {
    if (ev.color) return ev.color;
    const stableKey =
      ev.id && String(ev.id).trim().length
        ? String(ev.id)
        : String(ev.title ?? '');
    let hash = 5381;
    for (let i = 0; i < stableKey.length; i++) {
      hash = (hash << 5) + hash + stableKey.charCodeAt(i);
    }
    const idx = Math.abs(hash) % this.eventPalette.length;
    return this.eventPalette[idx] || res?.color || '#1677ff';
  }

  // Live now signal to drive the "now line"
  private _now = signal<Date>(new Date());
  private _nowTimer?: any;

  readonly inRange = computed(() => {
    const now = this._now().getTime();
    return now >= this.startDate().getTime() && now <= this.endDate().getTime();
  });

  // Percentage along the time axis from start to end (0..100)
  readonly nowPercent = computed(() => {
    const start = this.startDate().getTime();
    const end = this.endDate().getTime();
    const now = this._now().getTime();
    if (end <= start) return 0;
    const pct = ((now - start) / (end - start)) * 100;
    return Math.max(0, Math.min(100, pct));
  });

  constructor() {
    // Re-bind scroll sync after view switches or slot/resource structure changes
    effect(() => {
      // Track inputs that can change the rendered scrollers
      void this.effectiveViewMode();
      void this.slots();
      void this.resources();
      // Defer until the view updates DOM
      queueMicrotask(() => this.setupScrollSync());
    });
  }

  // Internal computed signals
  readonly startDate: Signal<Date> = computed(() => new Date(this.start()));
  readonly endDate: Signal<Date> = computed(() => new Date(this.end()));

  readonly totalMinutes = computed(() =>
    Math.max(
      0,
      Math.floor(
        (this.endDate().getTime() - this.startDate().getTime()) / 60000
      )
    )
  );

  readonly slots = computed(() => {
    const slots: { index: number; start: Date; end: Date; label: string }[] =
      [];
    const slot = this.effectiveSlotMinutes();
    const start = this.startDate();
    const total = this.totalMinutes();
    const count = Math.max(0, Math.ceil(total / slot));
    for (let i = 0; i < count; i++) {
      const s = new Date(start.getTime() + i * slot * 60000);
      const e = new Date(s.getTime() + slot * 60000);
      const label = s.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      slots.push({ index: i, start: s, end: e, label });
    }
    return slots;
  });

  // Events grouped by resource with overlap lane calculations so concurrent events render side-by-side
  readonly eventsByResource = computed(() => {
    type LaidOut = SchedulerEvent & {
      _start: Date;
      _end: Date;
      _lane: number;
      _lanes: number;
    };
    const byRes: Record<string, LaidOut[]> = {};
    // group raw
    const temp: Record<
      string,
      (SchedulerEvent & { _start: Date; _end: Date })[]
    > = {};
    for (const ev of this.events()) {
      const _start = new Date(ev.start);
      const _end = new Date(ev.end);
      if (_end <= this.startDate() || _start >= this.endDate()) continue; // out of range
      (temp[ev.resourceId] ||= []).push({ ...ev, _start, _end });
    }
    // per resource, sort and split into overlapping clusters, assign lanes per cluster
    for (const key of Object.keys(temp)) {
      const list = temp[key].sort(
        (a, b) => a._start.getTime() - b._start.getTime()
      );
      let cluster: typeof list = [];
      let clusterEnd = -Infinity;
      const laid: LaidOut[] = [];
      const flushCluster = () => {
        if (!cluster.length) return;
        // assign lanes in this cluster
        const laneEnds: number[] = [];
        // keep track of per-event lane index
        const laneOf = new Map<(typeof cluster)[number], number>();
        for (const ev of cluster) {
          // reuse first lane that is free (ended <= ev._start)
          let i = laneEnds.findIndex((end) => end <= ev._start.getTime());
          if (i === -1) {
            i = laneEnds.length;
            laneEnds.push(ev._end.getTime());
          } else {
            laneEnds[i] = ev._end.getTime();
          }
          laneOf.set(ev, i);
        }
        const totalLanes = Math.max(1, laneEnds.length);
        for (const ev of cluster) {
          laid.push({ ...ev, _lane: laneOf.get(ev) ?? 0, _lanes: totalLanes });
        }
        cluster = [];
        clusterEnd = -Infinity;
      };
      for (const ev of list) {
        if (!cluster.length) {
          cluster.push(ev);
          clusterEnd = ev._end.getTime();
        } else if (ev._start.getTime() < clusterEnd) {
          // overlaps with current cluster (transitively)
          cluster.push(ev);
          if (ev._end.getTime() > clusterEnd) clusterEnd = ev._end.getTime();
        } else {
          // no overlap with current cluster; finalize and start new
          flushCluster();
          cluster.push(ev);
          clusterEnd = ev._end.getTime();
        }
      }
      flushCluster();
      byRes[key] = laid;
    }
    return byRes;
  });

  // Helpers to compute CSS placement in grids
  slotIndexFor(date: Date): number {
    const minutesFromStart = Math.max(
      0,
      Math.floor((date.getTime() - this.startDate().getTime()) / 60000)
    );
    return Math.floor(minutesFromStart / this.effectiveSlotMinutes());
  }

  // Ceil variant: index of the first slot strictly after the given time unless it aligns exactly
  private slotIndexCeil(date: Date): number {
    const minutesFromStart = Math.max(
      0,
      Math.floor((date.getTime() - this.startDate().getTime()) / 60000)
    );
    return Math.ceil(minutesFromStart / this.effectiveSlotMinutes());
  }

  // Whether a given date is at the start of an hour (used for showing hour-only labels)
  isHourStart(date: Date): boolean {
    try {
      return date.getMinutes() === 0;
    } catch {
      return false;
    }
  }

  eventSpanSlots(ev: { _start: Date; _end: Date }): number {
    const startIdx = this.slotIndexFor(ev._start);
    const endIdx = Math.min(this.slots().length, this.slotIndexCeil(ev._end));
    return Math.max(1, endIdx - startIdx);
  }

  // Offset (px) of the event from the top of its starting slot, so its start time
  // is honoured exactly instead of snapping to the slot boundary.
  eventOffsetPx(ev: { _start: Date }): number {
    const startMin =
      (new Date(ev._start).getTime() - this.startDate().getTime()) / 60000;
    const slotIdx = Math.floor(startMin / this.effectiveSlotMinutes());
    const offsetMin = startMin - slotIdx * this.effectiveSlotMinutes();
    return Math.max(0, offsetMin * this.pxPerMinute());
  }

  // Size (px) of the event along the time axis, proportional to its real duration.
  eventSizePx(ev: { _start: Date; _end: Date }): number {
    const durMin =
      (new Date(ev._end).getTime() - new Date(ev._start).getTime()) / 60000;
    return Math.max(16, durMin * this.pxPerMinute());
  }

  // Whether a time span covers the given slot index (inclusive of start, exclusive of end)
  coversSlot(
    start: Date,
    end: Date,
    slotIndex: number | null | undefined
  ): boolean {
    if (slotIndex == null) return false;
    const sIdx = this.slotIndexFor(start);
    const eIdx = Math.max(sIdx + 1, this.slotIndexCeil(end));
    return slotIndex >= sIdx && slotIndex < eIdx;
  }

  // Working hours helpers
  private parseMaybe(dateOrStr?: Date | string): Date | undefined {
    if (!dateOrStr) return undefined;
    return new Date(dateOrStr);
  }

  hasBreakOverlap(
    res: SchedulerResource,
    slot: { start: Date; end: Date }
  ): boolean {
    const breaks = res.breaks || [];
    for (const br of breaks) {
      const bs = this.parseMaybe(br.start)!;
      const be = this.parseMaybe(br.end)!;
      if (slot.start < be && slot.end > bs) return true;
    }
    return false;
  }

  isSlotAvailable(
    res: SchedulerResource,
    slot: { start: Date; end: Date }
  ): boolean {
    const ws = this.parseMaybe(res.workStart);
    const we = this.parseMaybe(res.workEnd);
    // If no working hours defined, consider all slots available unless they overlap a break
    const withinWork = ws && we ? slot.start >= ws && slot.end <= we : true;

    if (!withinWork) return false;

    // Exclude any slot that overlaps a defined break
    if (this.hasBreakOverlap(res, slot)) return false;

    return true;
  }

  // Precompute availability once per (resources, slots, breaks) change so the
  // template doesn't recompute break overlaps for every cell on every CD cycle.
  readonly availabilityByResource = computed(() => {
    const slots = this.slots();
    const map: Record<string, { available: boolean[]; off: boolean[] }> = {};
    for (const res of this.resources()) {
      const available: boolean[] = [];
      const off: boolean[] = [];
      for (const sl of slots) {
        const isAvail = this.isSlotAvailable(res, sl);
        available.push(isAvail);
        // "off" = unavailable due to working hours, not a break overlay
        off.push(!isAvail && !this.hasBreakOverlap(res, sl));
      }
      map[res.id] = { available, off };
    }
    return map;
  });

  slotAvailable(resourceId: string, slotIndex: number): boolean {
    return (
      this.availabilityByResource()[resourceId]?.available[slotIndex] ?? true
    );
  }

  slotOff(resourceId: string, slotIndex: number): boolean {
    return this.availabilityByResource()[resourceId]?.off[slotIndex] ?? false;
  }

  // Compute breaks grouped by resource to render overlays
  readonly breaksByResource = computed(() => {
    const map: Record<
      string,
      { _start: Date; _end: Date; title?: string; color?: string }[]
    > = {};
    for (const res of this.resources()) {
      if (!res.breaks || !res.breaks.length) continue;
      for (const br of res.breaks) {
        const _start = new Date(br.start);
        const _end = new Date(br.end);
        if (_end <= this.startDate() || _start >= this.endDate()) continue;
        (map[res.id] ||= []).push({
          _start,
          _end,
          title: br.title,
          color: br.color,
        });
      }
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => a._start.getTime() - b._start.getTime());
    }
    return map;
  });

  // Non-working time bands (before workStart / after workEnd) per resource, clipped
  // to the visible range. Rendered as time-proportional overlays instead of greying
  // whole slot cells, so the working-hours edges are exact.
  readonly offBandsByResource = computed(() => {
    const rangeStart = this.startDate();
    const rangeEnd = this.endDate();
    const map: Record<string, { _start: Date; _end: Date }[]> = {};
    for (const res of this.resources()) {
      const ws = res.workStart ? new Date(res.workStart) : undefined;
      const we = res.workEnd ? new Date(res.workEnd) : undefined;
      if (!ws || !we) continue; // no working hours defined → fully available
      const bands: { _start: Date; _end: Date }[] = [];
      if (ws > rangeStart) {
        bands.push({ _start: rangeStart, _end: ws < rangeEnd ? ws : rangeEnd });
      }
      if (we < rangeEnd) {
        bands.push({
          _start: we > rangeStart ? we : rangeStart,
          _end: rangeEnd,
        });
      }
      if (bands.length) map[res.id] = bands;
    }
    return map;
  });

  // Click handlers
  onSlotClick(resourceId: string, slot: { start: Date; end: Date }) {
    const res = this.resources().find((r) => r.id === resourceId);
    if (res && !this.isSlotAvailable(res, slot)) return; // ignore clicks on unavailable slots
    this.slotClick.emit({ resourceId, start: slot.start, end: slot.end });
  }

  onSlotContextMenu(
    resourceId: string,
    slot: { start: Date; end: Date },
    e: MouseEvent
  ) {
    e.preventDefault();
    const res = this.resources().find((r) => r.id === resourceId);
    this.slotContextMenu.emit({
      resourceId,
      start: slot.start,
      end: slot.end,
      available: res ? this.isSlotAvailable(res, slot) : true,
      originalEvent: e,
    });
  }

  private _justDragged = false;

  onEventClick(ev: SchedulerEvent) {
    this.eventClick.emit(ev);
  }

  onEventClickGuard(ev: SchedulerEvent) {
    if (ev.extendedProps?.['isHold']) return; // holds are not bookings — nothing to open
    if (this.dragging() || this._justDragged) return; // suppress click if dragging or just finished a drag
    this.onEventClick(ev);
  }

  // ---- Drag & Drop (move event) ----
  dragging = signal(false);
  dragEvent = signal<
    | (SchedulerEvent & {
        _start?: Date;
        _end?: Date;
        _lanes?: number;
        _lane?: number;
      })
    | undefined
  >(undefined);
  private _unlistenMove?: () => void;
  private _unlistenUp?: () => void;
  dragPreview = signal<{
    resourceIndex: number;
    slotIndex: number;
    spanSlots: number;
  } | null>(null);

  // ---- Resize ----
  resizing = signal(false);
  resizeEvent = signal<
    (SchedulerEvent & { _start?: Date; _end?: Date }) | undefined
  >(undefined);
  resizeEdge = signal<'start' | 'end' | null>(null);
  resizePreview = signal<{
    resourceIndex: number;
    slotIndex: number;
    spanSlots: number;
  } | null>(null);

  protected isDragEnabled(): boolean {
    return this.mergedConfig().dragDrop ?? true;
  }

  protected isResizeEnabled(): boolean {
    return this.mergedConfig().resize ?? true;
  }

  // ---- Resize handlers ----
  onResizePointerDown(
    evObj: SchedulerEvent,
    edge: 'start' | 'end',
    pe: PointerEvent
  ) {
    if (!this.isResizeEnabled()) return;
    if (evObj.extendedProps?.['isAdditionalEmployee']) return; // read-only mirror event
    if (evObj.extendedProps?.['isHold']) return; // holds are not resizable
    const isMouse = pe.pointerType === 'mouse' || typeof pe.button === 'number';
    if (isMouse && pe.button !== 0) return;
    pe.stopPropagation();
    try {
      pe.preventDefault();
    } catch (err) {
      /* noop */
    }

    this.resizing.set(true);
    this.resizeEvent.set(evObj);
    this.resizeEdge.set(edge);

    try {
      (pe.target as Element).setPointerCapture?.(pe.pointerId);
    } catch (err) {
      /* noop */
    }

    const move = (e: PointerEvent) => {
      this.updateResizePreviewFromClient(e.clientX, e.clientY, evObj, edge);
    };
    const up = (e: PointerEvent) => {
      try {
        (pe.target as Element).releasePointerCapture?.(pe.pointerId);
      } catch (err) {
        /* noop */
      }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      this.finishResize(evObj, edge, e as unknown as MouseEvent);
    };
    const cancel = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      // reset
      this.resizing.set(false);
      this.resizeEvent.set(undefined);
      this.resizeEdge.set(null);
      this.resizePreview.set(null);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
    this._unlistenMove = () => window.removeEventListener('pointermove', move);
    this._unlistenUp = () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }

  private updateResizePreviewFromClient(
    clientX: number,
    clientY: number,
    ev: SchedulerEvent,
    edge: 'start' | 'end'
  ) {
    const indices = this.computeTargetIndicesFromClient(clientX, clientY);
    if (!indices) {
      this.resizePreview.set(null);
      return;
    }
    const fixedStartIdx = this.slotIndexFor(new Date(ev.start));
    const fixedEndIdx = this.slotIndexFor(new Date(ev.end));
    let newStartIdx = fixedStartIdx;
    let newEndIdx = fixedEndIdx;
    if (edge === 'end') {
      newEndIdx = Math.max(fixedStartIdx + 1, indices.slotIndex + 1);
    } else {
      newStartIdx = Math.min(fixedEndIdx - 1, indices.slotIndex);
    }
    const spanSlots = Math.max(1, newEndIdx - newStartIdx);
    // resource index for preview remains the current resource
    const resIndex = this.resources().findIndex((r) => r.id === ev.resourceId);
    this.resizePreview.set({
      resourceIndex: Math.max(0, resIndex),
      slotIndex: newStartIdx,
      spanSlots,
    });
  }

  private finishResize(
    evObj: SchedulerEvent,
    edge: 'start' | 'end',
    e: MouseEvent
  ) {
    const target = this.computeDropTarget(e);
    // clear preview state
    const originalResId = evObj.resourceId;
    const res = this.resources().find((r) => r.id === originalResId);
    const slots = this.slots();
    if (!res || !slots.length) {
      this.resizing.set(false);
      this.resizeEvent.set(undefined);
      this.resizeEdge.set(null);
      this.resizePreview.set(null);
      return;
    }
    const fixedStartIdx = this.slotIndexFor(new Date(evObj.start));
    const fixedEndIdx = this.slotIndexFor(new Date(evObj.end));
    let newStartIdx = fixedStartIdx;
    let newEndIdx = fixedEndIdx;
    // if no target, keep previous preview-derived indices if any
    if (target) {
      if (edge === 'end') {
        newEndIdx = Math.max(fixedStartIdx + 1, target.slotIndex + 1);
      } else {
        newStartIdx = Math.min(fixedEndIdx - 1, target.slotIndex);
      }
    } else if (this.resizePreview()) {
      newStartIdx = this.resizePreview()!.slotIndex;
      newEndIdx = newStartIdx + this.resizePreview()!.spanSlots;
    }

    newStartIdx = Math.max(0, Math.min(slots.length - 1, newStartIdx));
    newEndIdx = Math.max(newStartIdx + 1, Math.min(slots.length, newEndIdx));

    const start = slots[newStartIdx].start;
    const end = slots[newEndIdx - 1]?.end ?? slots[newStartIdx].end;

    // availability check within same resource
    if (!this.areAllSlotsAvailable(res, start, end)) {
      // just cancel if not available
      this.resizing.set(false);
      this.resizeEvent.set(undefined);
      this.resizeEdge.set(null);
      this.resizePreview.set(null);
      return;
    }

    this.eventResize.emit({
      event: evObj,
      resourceId: originalResId,
      start,
      end,
    });

    // reset
    this.resizing.set(false);
    this.resizeEvent.set(undefined);
    this.resizeEdge.set(null);
    this.resizePreview.set(null);
  }

  onEventMouseDown(evObj: SchedulerEvent, mouse: MouseEvent) {
    if (!this.isDragEnabled()) return;
    if (mouse.button !== 0) return; // left click only
    mouse.stopPropagation();
    this.startDrag(evObj);

    const move = (e: MouseEvent) => {
      // no live preview for minimal implementation
    };
    const up = (e: MouseEvent) => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      this.finishDrag(evObj, e);
    };

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up, { once: true });
    this._unlistenMove = () => document.removeEventListener('mousemove', move);
    this._unlistenUp = () => document.removeEventListener('mouseup', up);
  }

  private _pendingDragEv?: SchedulerEvent | null;
  private _dragStartPos?: { x: number; y: number };

  onEventPointerDown(evObj: SchedulerEvent, pe: PointerEvent) {
    if (!this.isDragEnabled()) return;
    if (evObj.extendedProps?.['isAdditionalEmployee']) return; // read-only mirror event
    if (evObj.extendedProps?.['isHold']) return; // holds are not draggable
    // support mouse and touch. For mouse ensure left button; for touch pointerType is 'touch'.
    const isMouse = pe.pointerType === 'mouse' || typeof pe.button === 'number';
    if (isMouse && pe.button !== 0) return;
    pe.stopPropagation();
    // prevent scrolling on touch while dragging
    try {
      pe.preventDefault();
    } catch {
      /* ignore */
    }

    this._pendingDragEv = evObj;
    this._dragStartPos = { x: pe.clientX, y: pe.clientY };

    // ensure we keep receiving move/up even if the user scrolls
    try {
      (pe.target as Element).setPointerCapture?.(pe.pointerId);
    } catch {
      /* ignore */
    }

    const threshold = 2; // px before we consider it a drag

    const move = (e: PointerEvent) => {
      if (!this.dragging() && this._pendingDragEv && this._dragStartPos) {
        const dx = e.clientX - this._dragStartPos.x;
        const dy = e.clientY - this._dragStartPos.y;
        if (Math.hypot(dx, dy) >= threshold) {
          this.startDrag(this._pendingDragEv);
          // initialize preview at first drag position
          this.updateDragPreviewFromClient(
            e.clientX,
            e.clientY,
            this._pendingDragEv
          );
        }
      } else if (this.dragging() && this.dragEvent()) {
        // update live preview while dragging
        this.updateDragPreviewFromClient(
          e.clientX,
          e.clientY,
          this.dragEvent()
        );
      }
    };

    const up = (e: PointerEvent) => {
      try {
        (pe.target as Element).releasePointerCapture?.(pe.pointerId);
      } catch {
        /* ignore */
      }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);

      // If we never started dragging, treat as a normal click (do nothing here; click handler will fire)
      if (!this.dragging()) {
        this._pendingDragEv = null;
        this._dragStartPos = undefined;
        return;
      }

      this.finishDrag(evObj, e as unknown as MouseEvent);
      this._pendingDragEv = null;
      this._dragStartPos = undefined;
    };

    const cancel = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      this.cancelActiveDrag();
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
    this._unlistenMove = () => window.removeEventListener('pointermove', move);
    this._unlistenUp = () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }

  private startDrag(evObj: SchedulerEvent) {
    this.dragging.set(true);
    this.dragEvent.set(evObj);
    this.eventDragStart.emit(evObj);
  }

  private finishDrag(evObj: SchedulerEvent, e: MouseEvent) {
    this._unlistenMove?.();
    this._unlistenUp?.();
    this._unlistenMove = this._unlistenUp = undefined;

    const drop = this.computeDropTarget(e);
    let dropped = false;
    if (drop) {
      const { resourceId, slotIndex } = drop;
      // duration in slots from original event
      const origStart = new Date(evObj.start);
      const origEnd = new Date(evObj.end);
      const durationSlots = Math.max(
        1,
        this.eventSpanSlots({ _start: origStart, _end: origEnd } as any)
      );
      const startSlot = this.slots()[slotIndex];
      if (startSlot) {
        const start = startSlot.start;
        const end = new Date(
          start.getTime() + durationSlots * this.effectiveSlotMinutes() * 60000
        );
        const targetResource = this.resources().find(
          (r) => r.id === resourceId
        );
        if (
          targetResource &&
          this.areAllSlotsAvailable(targetResource, start, end)
        ) {
          this.eventDrop.emit({
            event: evObj,
            resourceId: targetResource.id,
            start,
            end,
          });
          dropped = true;
        }
      }
    }

    this.dragging.set(false);
    this.dragEvent.set(undefined);
    this.dragPreview.set(null);
    // set a short suppression window to avoid opening popover/click after drag
    this._justDragged = true;
    setTimeout(() => {
      this._justDragged = false;
    }, 250);
    this.eventDragEnd.emit(dropped ? evObj : null);
  }

  private cancelActiveDrag() {
    this._unlistenMove?.();
    this._unlistenUp?.();
    this._unlistenMove = this._unlistenUp = undefined;
    this.dragging.set(false);
    this.dragEvent.set(undefined);
    this.dragPreview.set(null);
    // also reset resize state if any
    this.resizing.set(false);
    this.resizeEvent.set(undefined);
    this.resizeEdge.set(null);
    this.resizePreview.set(null);
    this._pendingDragEv = null;
    this._dragStartPos = undefined;
    this.eventDragEnd.emit(null);
  }

  private areAllSlotsAvailable(
    res: SchedulerResource,
    start: Date,
    end: Date
  ): boolean {
    // iterate over slot spans
    const idx = this.slotIndexFor(start);
    const endIdx = Math.max(idx + 1, this.slotIndexCeil(end));
    for (let i = idx; i < endIdx; i++) {
      const sl = this.slots()[i];
      if (!sl) return false;
      if (!this.isSlotAvailable(res, sl)) return false;
    }
    return true;
  }

  private updateDragPreviewFromClient(
    clientX: number,
    clientY: number,
    ev: SchedulerEvent | undefined | null
  ) {
    if (!ev) return;
    const indices = this.computeTargetIndicesFromClient(clientX, clientY);
    if (!indices) {
      this.dragPreview.set(null);
      return;
    }
    const durationSlots = Math.max(
      1,
      this.eventSpanSlots({
        _start: new Date(ev.start),
        _end: new Date(ev.end),
      } as any)
    );
    this.dragPreview.set({
      resourceIndex: indices.resourceIndex,
      slotIndex: indices.slotIndex,
      spanSlots: durationSlots,
    });
  }

  private computeTargetIndicesFromClient(
    clientX: number,
    clientY: number
  ): {
    resourceIndex: number;
    slotIndex: number;
  } | null {
    const grid = this.grid()?.nativeElement;
    const scroller = this.bodyScroller()?.nativeElement;
    if (!grid || !scroller) return null;
    const rect = grid.getBoundingClientRect();

    const anyCell = grid.querySelector('.grid-cell') as HTMLElement | null;
    if (!anyCell) return null;
    const cellRect = anyCell.getBoundingClientRect();
    const cellW = Math.max(1, cellRect.width);
    const cellH = Math.max(1, cellRect.height);

    const x = clientX - rect.left + scroller.scrollLeft;
    const vScroll = this.schedulerBody()?.nativeElement?.scrollTop ?? 0;
    const y = clientY - rect.top + vScroll;

    const resCount = this.resources().length;
    const slotCount = this.slots().length;
    const vm = this.effectiveViewMode();

    if (vm === 'horizontal') {
      const col = Math.floor(x / cellW);
      const row = Math.floor(y / cellH);
      if (col < 0 || row < 0 || col >= resCount || row >= slotCount)
        return null;
      return { resourceIndex: col, slotIndex: row };
    } else {
      const col = Math.floor(x / cellW);
      const row = Math.floor(y / cellH);
      if (col < 0 || row < 0 || col >= slotCount || row >= resCount)
        return null;
      return { resourceIndex: row, slotIndex: col };
    }
  }

  private computeDropTarget(
    e: MouseEvent
  ): { resourceId: string; slotIndex: number } | null {
    const grid = this.grid()?.nativeElement;
    const scroller = this.bodyScroller()?.nativeElement;
    if (!grid || !scroller) return null;
    const rect = grid.getBoundingClientRect();
    const vm = this.effectiveViewMode();

    // measure one cell to get dimensions
    const anyCell = grid.querySelector('.grid-cell') as HTMLElement | null;
    if (!anyCell) return null;
    const cellRect = anyCell.getBoundingClientRect();
    const cellW = Math.max(1, cellRect.width);
    const cellH = Math.max(1, cellRect.height);

    const x = e.clientX - rect.left + scroller.scrollLeft;
    const vScroll = this.schedulerBody()?.nativeElement?.scrollTop ?? 0;
    const y = e.clientY - rect.top + vScroll;

    const resCount = this.resources().length;
    const slotCount = this.slots().length;

    if (vm === 'horizontal') {
      const col = Math.floor(x / cellW);
      const row = Math.floor(y / cellH);
      if (col < 0 || row < 0 || col >= resCount || row >= slotCount)
        return null;
      const resourceId = this.resources()[col].id;
      const slotIndex = row;
      return { resourceId, slotIndex };
    } else {
      // vertical view: time on columns, resources on rows
      const col = Math.floor(x / cellW);
      const row = Math.floor(y / cellH);
      if (col < 0 || row < 0 || col >= slotCount || row >= resCount)
        return null;
      const resourceId = this.resources()[row].id;
      const slotIndex = col;
      return { resourceId, slotIndex };
    }
  }

  // Sync header/body horizontal scrolling
  ngAfterViewInit(): void {
    // start/update timer to refresh the now line periodically
    if (this._nowTimer) clearInterval(this._nowTimer);
    // Update every 30 seconds to keep it reasonably fresh without being heavy
    this._nowTimer = setInterval(() => this._now.set(new Date()), 30000);
    // also do an initial immediate update
    queueMicrotask(() => this._now.set(new Date()));
    this.setupScrollSync();
  }

  private _cleanups: Array<() => void> = [];

  private setupScrollSync(): void {
    // remove previous listeners
    this._cleanups.forEach((fn) => {
      try {
        fn();
      } catch {
        /* noop */
      }
    });
    this._cleanups = [];

    const header = this.headerScroller()?.nativeElement;
    const body = this.bodyScroller()?.nativeElement;
    if (!header || !body) return;

    const syncFromBody = () => {
      header.scrollLeft = body.scrollLeft;
    };
    const syncFromHeader = () => {
      body.scrollLeft = header.scrollLeft;
    };

    body.addEventListener('scroll', syncFromBody);
    header.addEventListener('scroll', syncFromHeader);

    this._cleanups.push(() => body.removeEventListener('scroll', syncFromBody));
    this._cleanups.push(() =>
      header.removeEventListener('scroll', syncFromHeader)
    );
  }

  ngOnDestroy(): void {
    if (this._nowTimer) {
      try {
        clearInterval(this._nowTimer);
      } catch {
        /* ignore */
      }
      this._nowTimer = undefined;
    }
    this._cleanups.forEach((fn) => {
      try {
        fn();
      } catch {
        /* noop */
      }
    });
    this._cleanups = [];
  }
}
